"use client";

import { useQueryClient } from "@tanstack/react-query";
import PointsLoader from "@/app/(components)/generics/loaders/PointsLoader/PointsLoader";
import Pagination from "@/app/(components)/generics/Pagination/Pagination";
import { hasPages } from "@/app/(components)/generics/Pagination/Pagination.logic";
import ProspectList from "@/app/(components)/specifics/tools/ProspectSearch/components/ProspectList/ProspectList";
import { useCanEditContent } from "@/app/(components)/specifics/wrappers/BrandProvider/BrandProvider";
import { getProspectCriteria } from "@/app/actions/prospect-search/get/action";
import { useProspectStatusActions } from "@/app/hooks/useProspectStatusActions";
import { ModalStore } from "@/app/stores/ModalStore";
import { TKey } from "@/app/translations/TKey";
import { PageParams } from "@/app/types/common/DefaultProps";
import { ProspectSearchData } from "@/app/types/prospect/ProspectSearchData";
import { ProspectsSectionState } from "@/app/types/prospect/ProspectsSectionState";
import { Limits } from "@/app/vars/Limits";
import { QueryKey } from "@/app/vars/QueryKeys";
import styles from "./ProspectsSection.module.css";

type Props = {
  params: PageParams;
  avatarId?: number;
  state: ProspectsSectionState;
  canAdd?: boolean;
  isPending?: boolean;
  openOnContext?: boolean;
};

export default function ProspectsSection(props: Props) {
  const {
    params,
    avatarId,
    state,
    canAdd = false,
    isPending = false,
    openOnContext = false,
  } = props;
  const { lang } = params;

  const queryClient = useQueryClient();
  const canEditContent = useCanEditContent();
  const prospectStatusActions = useProspectStatusActions(params);

  const openProspectDetail = (prospectId: number, fullName: string) => {
    ModalStore.getState().openProspectDetail(params, prospectId, fullName, true, openOnContext);
  };

  const openProspectHistory = (prospectId: number, fullName: string) => {
    ModalStore.getState().openProspectDetail(params, prospectId, fullName, true, false, true);
  };

  const handleAddProspect = async () => {
    if (!avatarId) {
      return;
    }

    const data = await queryClient.ensureQueryData<ProspectSearchData | null>({
      queryKey: [QueryKey.PROSPECT_CRITERIA, avatarId],
      queryFn: () => getProspectCriteria(avatarId),
    });

    ModalStore.getState().openProspectForm(params, avatarId, data?.criteria ?? null, () => {
      state.resetPage();
      queryClient.invalidateQueries({ queryKey: [QueryKey.CLIENT_PROSPECTS, avatarId] });
    });
  };

  if (state.isLoading) {
    return (
      <div className={styles.loaderContainer}>
        <PointsLoader color="var(--color-green-3)" delay={Limits.PROSPECT_SEARCH_LOADER_DELAY} />
      </div>
    );
  }

  if (!state.hasList) {
    return (
      <ProspectList
        lang={lang}
        isConnected={true}
        isPending={isPending}
        onAddProspect={canAdd ? handleAddProspect : undefined}
      />
    );
  }

  return (
    <>
      <ProspectList
        lang={lang}
        isConnected={true}
        isPending={isPending}
        prospects={state.prospects}
        isLoading={state.isStale}
        count={state.total}
        emptyTextKey={TKey.PROSPECT_SEARCH_FILTER_EMPTY}
        tab={state.tab}
        onTabChange={state.changeTab}
        actionTypes={state.actionTypes}
        actionFilter={state.actionFilter}
        onActionFilterChange={state.changeActionFilter}
        onSelectAction={canEditContent ? state.selectAction : undefined}
        onCreateActionType={canEditContent ? state.createActionType : undefined}
        onOpenHistory={openProspectHistory}
        onRowClick={openProspectDetail}
        onAddProspect={canAdd ? handleAddProspect : undefined}
        onQualify={canEditContent ? prospectStatusActions.qualify : undefined}
        onReject={canEditContent ? prospectStatusActions.reject : undefined}
      />

      {hasPages(state.pagination) && (
        <div className={styles.pagination}>
          <Pagination data={state.pagination} onPageChange={state.setPage} maxVisiblePages={5} />
        </div>
      )}
    </>
  );
}
